import { createEvent } from 'effector';
import { track } from './amplitude';
import {
  POI_ARTIFACT_OPENED,
  POI_ARTIFACT_CLOSED,
  INSIDE_SCENE_ENTERED,
  INSIDE_SCENE_EXITED,
  NEXT_SCENE_CHIP_CLICKED,
  SCENE_BANNER_CLICKED,
  INSIDE_POI_CLICKED,
} from './events';
import {
  openPoiArtifact,
  closePoiArtifact,
  enterScene,
  rebuildStack,
} from '../state/sceneNavigation';
import { currentSceneId } from './trackerContext';
import { $nextSceneIdForCurrentScene } from '../state/visitedSights';
import { isPoiArtifactCached } from '../scene/glb/poiArtifactHost';
import {
  isInsideScene,
  INSIDE_SCENE_PARENT,
} from '../scene/insideScenes/registry';
import { resolveMainBannerSceneUrlId } from '../scene/sceneBannerMap';

/**
 * UI-инициируемые события для аналитики. Дёргаются явно из обработчиков
 * кликов (`AssistantHud`, `OverallSceneOverlay`, `App.tsx`). Отдельные от
 * state-событий навигации (`enterScene` / `goBack`), потому что последние
 * также вызываются программно (rebuildStack, runNavigationCinematic) — а
 * для аналитики важно различать «юзер кликнул в баннер» vs «приложение
 * перетекло между сценами».
 */
export const sceneBannerClicked = createEvent<{
  surface: 'chat' | 'overall-overlay';
  bannerKind: 'main' | 'inside';
  targetSceneId: string;
}>();

export const nextSceneChipClicked = createEvent<{
  targetSceneId: string;
}>();

export const insideSceneBackPressed = createEvent<{
  sceneId: string;
  parentSceneId: string | null;
}>();

export const insidePoiClicked = createEvent<{
  sightId: string;
  sightName: string | null;
  targetSceneId: string;
}>();

/**
 * Нормализует sceneId в URL-форму для сравнения. Бэк присылает next-scene
 * в одной из трёх форм (main backend-id / inside-id / URL-id main-сцены).
 * Чтобы сравнить target баннера с подсказанным next-scene, сводим оба к
 * URL-id (для main-сцен) или оставляем как есть (для inside-сцен).
 */
const toComparableSceneId = (id: string | null): string | null => {
  if (id == null) return null;
  return resolveMainBannerSceneUrlId(id) ?? id;
};

let poiArtifactOpenedAt: number | null = null;
let poiArtifactSceneIdAtOpen: string | null = null;
let poiArtifactParentSceneIdAtOpen: string | null = null;

export const startNavigationTracking = () => {
  // --- POI Artifact open/close ---

  openPoiArtifact.watch(({ sceneId }) => {
    poiArtifactOpenedAt = performance.now();
    poiArtifactSceneIdAtOpen = sceneId;
    poiArtifactParentSceneIdAtOpen = currentSceneId();
    track(POI_ARTIFACT_OPENED, {
      artifact_scene_id: sceneId,
      parent_scene_id: poiArtifactParentSceneIdAtOpen,
      was_cached: isPoiArtifactCached(sceneId),
    });
  });

  closePoiArtifact.watch(() => {
    const durationMs =
      poiArtifactOpenedAt != null
        ? Math.round(performance.now() - poiArtifactOpenedAt)
        : null;
    track(POI_ARTIFACT_CLOSED, {
      artifact_scene_id: poiArtifactSceneIdAtOpen,
      parent_scene_id: poiArtifactParentSceneIdAtOpen,
      duration_ms: durationMs,
    });
    poiArtifactOpenedAt = null;
    poiArtifactSceneIdAtOpen = null;
    poiArtifactParentSceneIdAtOpen = null;
  });

  // --- Inside scene entered (state-based — ловит ВСЕ entries) ---
  //
  // Покрывает все пути: клик в баннер чата, клик в overlay-баннер,
  // клик в next-scene chip, deep-link на /kaaba/kaaba-inside.
  // Click-source трекается отдельно через `sceneBannerClicked` /
  // `nextSceneChipClicked`.

  enterScene.watch((entry) => {
    if (!isInsideScene(entry.sceneId)) return;
    track(INSIDE_SCENE_ENTERED, {
      scene_id: entry.sceneId,
      parent_scene_id: INSIDE_SCENE_PARENT[entry.sceneId] ?? null,
    });
  });

  rebuildStack.watch((stack) => {
    const top = stack[stack.length - 1];
    if (!top || !isInsideScene(top.sceneId)) return;
    track(INSIDE_SCENE_ENTERED, {
      scene_id: top.sceneId,
      parent_scene_id: INSIDE_SCENE_PARENT[top.sceneId] ?? null,
    });
  });

  // --- Inside scene exited (back button) ---

  insideSceneBackPressed.watch(({ sceneId, parentSceneId }) => {
    track(INSIDE_SCENE_EXITED, {
      scene_id: sceneId,
      parent_scene_id: parentSceneId,
    });
  });

  // --- User-action: next scene chip click ---

  nextSceneChipClicked.watch(({ targetSceneId }) => {
    track(NEXT_SCENE_CHIP_CLICKED, {
      target_scene_id: targetSceneId,
      from_scene_id: currentSceneId(),
    });
  });

  // --- User-action: scene banner card click ---
  //
  // `is_next_scene_hint` помечает клики, цель которых совпадает с
  // подсказанной бэкендом следующей сценой (из tour-item `{kind:'scene'}`).
  // Позволяет в Amplitude собрать единый funnel `Next Scene Hinted → ...`
  // независимо от того, чем юзер воспользовался: золотой chip-кнопкой
  // (`Next Scene Chip Clicked`) или баннером в чате / overlay
  // (`Scene Banner Clicked` with `is_next_scene_hint=true`).

  // --- User-action: 3D POI marker click that enters an inside-scene ---
  //
  // Same target as `Scene Banner Clicked banner_kind='inside'` (and the
  // resulting `Inside Scene Entered` state-event), but emitted from the
  // 3D POI surface so we can attribute entries by source (marker vs banner)
  // and per-sight.

  insidePoiClicked.watch(({ sightId, sightName, targetSceneId }) => {
    track(INSIDE_POI_CLICKED, {
      sight_id: sightId,
      sight_name: sightName,
      target_scene_id: targetSceneId,
      from_scene_id: currentSceneId(),
    });
  });

  sceneBannerClicked.watch(({ surface, bannerKind, targetSceneId }) => {
    const hinted = $nextSceneIdForCurrentScene.getState();
    const isNextSceneHint =
      hinted !== null &&
      toComparableSceneId(targetSceneId) === toComparableSceneId(hinted);
    track(SCENE_BANNER_CLICKED, {
      surface,
      banner_kind: bannerKind,
      target_scene_id: targetSceneId,
      from_scene_id: currentSceneId(),
      is_next_scene_hint: isNextSceneHint,
    });
  });
};
